import { useMemo } from 'react'

import { Icon } from '@wartek-id/icon-v2'
import { Text } from '@wartek-id/text-v1'

import { assetUrl } from 'utils/url'
import { Header } from 'app/KinerjaShared/components/Header'
import CardPeriod from 'app/KinerjaShared/components/CardPeriod'
import { IndicatorCard } from 'app/KinerjaPage/pages/PelaksanaanV2/components/IndicatorCard'
import { useKinerjaTipe } from 'app/KinerjaShared/hooks/useKinerjaTipe'
import { useAPIPelaksanaanProgress } from 'api/kinerja/useAPIKinerja'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import { PraktikKinerjaStepCard } from 'app/KinerjaShared/components/PraktikKinerjaStepCard'
import { PageGuardGpkPelaksanaanStatus } from 'app/KinerjaGpkPage/components/PageGuardGpkPelaksanaanStatus'
import { PersiapanStepCard } from 'app/KinerjaGpkPage/pages/pegawai/pelaksanaan/HubPraktikKinerja/PersiapanStepCard'
import { ObservasiStepCard } from 'app/KinerjaGpkPage/pages/pegawai/pelaksanaan/HubPraktikKinerja/ObservasiStepCard'
import {
  SectionCard,
  SectionCardHeader,
  SectionCardBody,
} from 'app/KinerjaShared/components/SectionCard'
import { GPK_PEGAWAI_PRAKTIK_KINERJA_ICONS } from 'app/KinerjaGpkPage/constants/contents'

import type { MultiPeriodPayload } from 'types/kinerja'

type HubPraktikKinerjaProps = {
  periodParams?: MultiPeriodPayload
}

export function HubPraktikKinerja({ periodParams }: HubPraktikKinerjaProps) {
  const { tipe } = useKinerjaTipe()
  const { periodNo, periodYear } = usePeriodContext()

  // -----------------------------------------
  // Resolve period params
  // -----------------------------------------
  const resolvedPeriodParams = useMemo(() => {
    if (periodParams) return periodParams

    // Default with current period
    return {
      skpPeriod: periodNo,
      skpYear: periodYear,
    }
  }, [periodParams, periodNo, periodYear])

  // -----------------------------------------
  // Get pelaksanaan progress data
  // -----------------------------------------
  const {
    isLoading: isPelaksanaanProgressLoading,
    data: pelaksanaanProgressData,
  } = useAPIPelaksanaanProgress({
    tipe,
    periodParams: {
      skp_period: resolvedPeriodParams.skpPeriod,
      skp_year: resolvedPeriodParams.skpYear,
    },
    opts: {
      staleTime: 60 * 60 * 1000, // 1 hour
    },
  })

  // -----------------------------------------
  // Resolve Praktik Kinerja asset path
  // -----------------------------------------
  const illustrationAssetPath = useMemo(() => {
    const icon =
      GPK_PEGAWAI_PRAKTIK_KINERJA_ICONS[
        pelaksanaanProgressData?.data?.subIndicatorName
      ]

    if (icon) return assetUrl(`/illustrations/${icon}`)

    return ''
  }, [pelaksanaanProgressData?.data?.subIndicatorName])

  return (
    <main className="pt-12 px-8 pb-8">
      <div className="mx-auto max-w-7xl">
        <PageGuardGpkPelaksanaanStatus periodParams={resolvedPeriodParams}>
          <div className="flex justify-between items-center">
            <Header title="Praktik Kinerja" className="mb-9" />
          </div>
          <SectionCard>
            <SectionCardHeader variant="gradient-blue">
              <Text as="h3" color="inherit" variant="heading-lg">
                Periode:{' '}
                <CardPeriod
                  period={resolvedPeriodParams?.skpPeriod}
                  year={resolvedPeriodParams?.skpYear}
                  enableMultiplePeriodSupport
                />
              </Text>
            </SectionCardHeader>
            <SectionCardBody className="p-6 space-y-12">
              <IndicatorCard
                illustrationAssetPath={illustrationAssetPath}
                subIndicatorName={
                  pelaksanaanProgressData?.data?.subIndicatorName
                }
                subIndicatorDefinition={
                  pelaksanaanProgressData?.data?.subIndicatorDefinition
                }
                qualityTarget={pelaksanaanProgressData?.data?.qualityTarget}
                contentId={pelaksanaanProgressData?.data?.contentId}
                isLoading={isPelaksanaanProgressLoading}
              />

              <div className="space-y-14">
                <div>
                  <Text variant="heading-sm" as="h2" className="mb-2">
                    Bagian 1: Pelaksanaan Observasi
                  </Text>

                  <div className="flex items-stretch gap-2">
                    <PersiapanStepCard
                      appealState={pelaksanaanProgressData?.data?.appealState}
                      latestFormState={
                        pelaksanaanProgressData?.data?.latestFormState
                      }
                      state={pelaksanaanProgressData?.data?.state}
                    />

                    <StepArrow />

                    <ObservasiStepCard
                      state={pelaksanaanProgressData?.data?.state}
                    />

                    <StepArrow />

                    <PraktikKinerjaStepCard
                      className="max-w-96"
                      iconSrc="/illustrations/img-document-interaction-discussion.png"
                      status="locked"
                      lockedHint="Menunggu aksi dari Tim Kinerja."
                      title={
                        <>
                          Pembinaan Kinerja: <br /> Isi Rencana Tindak Lanjut
                        </>
                      }
                      description="Refleksi dan diskusi dengan Tim Kinerja untuk isi dukungan yang dibutuhkan."
                    />
                  </div>
                </div>
                <div>
                  <Text variant="heading-sm" as="h2" className="mb-2">
                    Bagian 2: Pelaksanaan Tindak Lanjut
                  </Text>

                  <div className="flex items-stretch gap-2">
                    <PraktikKinerjaStepCard
                      className="max-w-96"
                      iconSrc="/illustrations/img-smartphone-pop-up-modal-cart-section.png"
                      status="locked"
                      lockedHint="Dilakukan setelah isi Rencana Tindak Lanjut."
                      title={
                        <>
                          Pembinaan Kinerja: <br /> Pelaksanaan Tindak Lanjut
                        </>
                      }
                      description="Laksanakan Pilihan Belajar dan tindak lanjut lainnya."
                    />

                    <StepArrow />

                    <PraktikKinerjaStepCard
                      className="max-w-96"
                      iconSrc="/illustrations/img-document-progress-bar-check.png"
                      status="locked"
                      lockedHint="Menunggu aksi dari Tim Kinerja."
                      title={
                        <>
                          Pembinaan Kinerja: <br /> Isi Refleksi dan Evaluasi
                          Tindak Lanjut
                        </>
                      }
                      description="Diskusi dengan Tim Kinerja untuk isi capaian, tantangan, dan upaya perbaikan."
                    />
                  </div>
                </div>
              </div>
            </SectionCardBody>
          </SectionCard>
        </PageGuardGpkPelaksanaanStatus>
      </div>
    </main>
  )
}

function StepArrow() {
  return (
    <div className="flex items-center">
      <Icon>arrow_forward</Icon>
    </div>
  )
}
